/**
 * Preview vs Generate gate helpers for SaaS Free / PAYG.
 * Preview is always allowed. Free uses starter limits (duration, retries, PV)
 * plus metered 400-credit pool; PAYG uses wallet credits.
 */

function freePoolExhausted(entitlements) {
  const poolAvailable = Number(entitlements?.freeCreditPool?.totalAvailable);
  if (Number.isFinite(poolAvailable)) {
    return poolAvailable <= 0;
  }
  return entitlements?.canGenerate === false;
}

/**
 * Remaining credits on the campaign actor (Free default-actor pool).
 * `null` means the pool did not include actor rows — caller should not block.
 */
export function resolveFreeActorCreditsAvailable(entitlements, opts = {}) {
  if (opts.actorCreditsAvailable != null && Number.isFinite(Number(opts.actorCreditsAvailable))) {
    return Math.max(0, Number(opts.actorCreditsAvailable));
  }
  const actors = entitlements?.freeCreditPool?.actors;
  if (!Array.isArray(actors) || actors.length === 0) {
    return null;
  }
  const actorId = opts.actorId != null ? Number(opts.actorId) : NaN;
  if (Number.isFinite(actorId)) {
    const match = actors.find((row) => Number(row.actorId) === actorId);
    if (match) {
      return Math.max(0, Number(match.available) || 0);
    }
    return 0;
  }
  return actors.reduce((max, row) => Math.max(max, Number(row.available) || 0), 0);
}

/** Map add-entry / manual-v3 credit failures to the Free recharge drawer. PAYG stays on actor-allocate copy. */
export function mapFreePostPublishEntryCreditError(payload, isFree) {
  if (!isFree) {
    return null;
  }
  const code =
    payload?.paywall?.code
    || payload?.details?.paywall?.code
    || payload?.code
    || payload?.details?.code
    || null;
  if (
    code === "FREE_CREDITS_EXHAUSTED"
    || code === "FREE_ACTOR_CREDITS_EXHAUSTED"
    || code === "INSUFFICIENT_ACTOR_CREDITS"
  ) {
    return "FREE_ACTOR_CREDITS_EXHAUSTED";
  }
  return null;
}

export function canPreviewAlways() {
  return true;
}

/** Free starter plan (not PAYG / Enterprise). */
export function isSaasFreePlan(entitlements) {
  if (!entitlements || entitlements.isPremium || entitlements.isEnterprise) {
    return false;
  }
  const freeCaps = entitlements?.freeCaps;
  if (freeCaps?.applies === false) {
    return false;
  }
  if (freeCaps?.applies === true) {
    return true;
  }
  return String(entitlements.plan || "free").toLowerCase() === "free";
}

/** Free published campaign video rows are credit-gated (no quantity cap). */
export const FREE_PUBLISHED_VIDEO_ENTRY_LIMIT = Number.POSITIVE_INFINITY;

/**
 * @param {object|null} entitlements — normalized SaaS entitlements
 * @param {{
 *   avatarTemplate?: boolean,
 *   durationSec?: number,
 *   retriesUsed?: number,
 *   hasGeneratedOnce?: boolean,
 * }} [opts]
 * @returns {{ allowed: boolean, reason: string|null, redirect: string|null, useDrawer: boolean }}
 */
export function resolveGenerateGate(entitlements, opts = {}) {
  const {
    avatarTemplate = false,
    durationSec = null,
    retriesUsed = 0,
    hasGeneratedOnce = false,
  } = opts;

  if (!entitlements) {
    return {
      allowed: false,
      reason: "NO_ENTITLEMENTS",
      redirect: "/paywalls/zero-credits",
      useDrawer: true,
    };
  }

  const freeCaps = entitlements.freeCaps;
  const isFree = Boolean(freeCaps?.applies) && !entitlements.isPremium && !entitlements.isEnterprise;

  if (isFree) {
    const rem = freeCaps.remaining || {};
    const maxVideoSec = Number(rem.maxVideoSec ?? 30);
    const maxRetries = Number(rem.maxAttemptsPerClip ?? rem.maxRetriesPerCampaign ?? 3);
    const retries = Math.max(0, Number(retriesUsed) || 0);

    if (durationSec != null && Number.isFinite(Number(durationSec)) && Number(durationSec) > maxVideoSec) {
      return {
        allowed: false,
        reason: "FREE_DURATION_EXCEEDED",
        redirect: "/billing/buy-credits",
        useDrawer: true,
      };
    }

    if (hasGeneratedOnce && retries >= maxRetries) {
      return {
        allowed: false,
        reason: "FREE_RETRY_EXHAUSTED",
        redirect: "/billing/buy-credits",
        useDrawer: true,
      };
    }

    if (avatarTemplate && !entitlements.canTryAvatarTemplate) {
      return {
        allowed: false,
        reason: "FREE_AVATAR_EXHAUSTED",
        redirect: "/paywalls/free-avatar",
        useDrawer: true,
      };
    }

    if (freePoolExhausted(entitlements)) {
      return {
        allowed: false,
        reason: "FREE_CREDITS_EXHAUSTED",
        redirect: "/billing/buy-credits",
        useDrawer: true,
      };
    }

    return { allowed: true, reason: null, redirect: null, useDrawer: false };
  }

  if (!entitlements.canGenerate) {
    return {
      allowed: false,
      reason: "ZERO_CREDITS",
      redirect: "/paywalls/zero-credits",
      useDrawer: true,
    };
  }

  if (avatarTemplate && !entitlements.canTryAvatarTemplate && !entitlements.isPremium) {
    return {
      allowed: false,
      reason: "FREE_AVATAR_EXHAUSTED",
      redirect: "/paywalls/free-avatar",
      useDrawer: true,
    };
  }

  return { allowed: true, reason: null, redirect: null, useDrawer: false };
}

/**
 * Whether Free plan can open Create Design for a new draft.
 */
export function resolveCreateDraftGate(entitlements) {
  const freeCaps = entitlements?.freeCaps;
  if (!freeCaps?.applies || entitlements?.isPremium || entitlements?.isEnterprise) {
    return { allowed: true, reason: null };
  }
  return { allowed: true, reason: null };
}

/**
 * Whether Free plan can render this draft. Render is unlimited (credit-gated elsewhere).
 * @param {object|null} entitlements
 * @param {{ draftId?: number|string }} [opts]
 */
export function resolveDraftRenderGate(entitlements, opts = {}) {
  if (!entitlements) {
    return {
      allowed: false,
      reason: "NO_ENTITLEMENTS",
      redirect: "/paywalls/zero-credits",
      useDrawer: true,
    };
  }
  return { allowed: true, reason: null, redirect: null, useDrawer: false };
}

/**
 * Whether Free plan can personalize this campaign/draft. Unlimited drafts (credit-gated).
 * @param {object|null} entitlements
 * @param {{ campaignId?: number|string }} [_opts]
 */
export function resolvePersonalizedCampaignGate(entitlements, _opts = {}) {
  return { allowed: true, reason: null };
}

/**
 * Post-publish actor video generate (run-bulk) — Free allows one video on one actor.
 * @param {object|null} entitlements
 * @param {{ regenerate?: boolean, retriesUsed?: number }} [opts]
 */
export function resolvePostPublishGenerateGate(entitlements, opts = {}) {
  const { regenerate = false, retriesUsed = 0 } = opts;

  if (!entitlements) {
    return {
      allowed: false,
      reason: "NO_ENTITLEMENTS",
      redirect: "/paywalls/zero-credits",
      useDrawer: true,
    };
  }

  const freeCaps = entitlements.freeCaps;
  const isFree = Boolean(freeCaps?.applies) && !entitlements.isPremium && !entitlements.isEnterprise;

  if (!isFree) {
    if (!entitlements.canGenerate) {
      return {
        allowed: false,
        reason: "ZERO_CREDITS",
        redirect: "/paywalls/zero-credits",
        useDrawer: true,
      };
    }
    return { allowed: true, reason: null, redirect: null, useDrawer: false };
  }

  const rem = freeCaps.remaining || {};
  const maxRetries = Number(rem.maxAttemptsPerClip ?? rem.maxRetriesPerCampaign ?? 3);

  if (regenerate) {
    if (Number(retriesUsed) >= maxRetries) {
      return {
        allowed: false,
        reason: "FREE_RETRY_EXHAUSTED",
        redirect: "/billing/buy-credits",
        useDrawer: true,
      };
    }
    return { allowed: true, reason: null, redirect: null, useDrawer: false };
  }

  if (freePoolExhausted(entitlements)) {
    return {
      allowed: false,
      reason: "FREE_CREDITS_EXHAUSTED",
      redirect: "/billing/buy-credits",
      useDrawer: true,
    };
  }

  return { allowed: true, reason: null, redirect: null, useDrawer: false };
}

/**
 * WhatsApp send — not available on Free after publish.
 * @param {object|null} entitlements
 */
export function resolvePostPublishWhatsAppGate(entitlements) {
  if (!entitlements) {
    return {
      allowed: false,
      reason: "NO_ENTITLEMENTS",
      redirect: "/paywalls/zero-credits",
      useDrawer: true,
    };
  }

  if (!isSaasFreePlan(entitlements)) {
    return { allowed: true, reason: null, redirect: null, useDrawer: false };
  }

  return {
    allowed: false,
    reason: "FREE_WHATSAPP_DENIED",
    redirect: "/billing/buy-credits",
    useDrawer: true,
  };
}

/**
 * Adding video entries (manual row, CSV) after publish.
 * Free: recharge when this actor has no credits left. PAYG / Enterprise: always allow add.
 * @param {object|null} entitlements
 * @param {{ existingCount?: number, addingCount?: number, actorId?: number|string, actorCreditsAvailable?: number }} [opts]
 */
export function resolvePostPublishVideoEntryGate(entitlements, opts = {}) {
  if (!entitlements) {
    return {
      allowed: false,
      reason: "NO_ENTITLEMENTS",
      redirect: "/paywalls/zero-credits",
      useDrawer: true,
    };
  }

  if (!isSaasFreePlan(entitlements)) {
    return { allowed: true, reason: null, redirect: null, useDrawer: false };
  }

  const actorCreditsAvailable = resolveFreeActorCreditsAvailable(entitlements, opts);
  if (actorCreditsAvailable != null && actorCreditsAvailable <= 0) {
    return {
      allowed: false,
      reason: "FREE_ACTOR_CREDITS_EXHAUSTED",
      redirect: "/billing/buy-credits",
      useDrawer: true,
    };
  }

  return { allowed: true, reason: null, redirect: null, useDrawer: false };
}

export default resolveGenerateGate;
